import { useMemo, type ReactNode } from 'react';
import type { ThemeRegistry } from '@classy/theme-sdk';
import { ThemeProvider } from './ThemeProvider.js';
import { getSessionThemeKey } from './themeStorage.js';
import sessionBg from '../../../../assets/auth/Sunrise.png';
import styles from './SessionBg.module.css';

export interface SessionBgProps {
  registry: ThemeRegistry;
  fallbackKey?: string;
  allowedThemeKeys?: readonly string[] | undefined;
  children: ReactNode;
}

/**
 * Wraps a hub-level subtree in a ThemeProvider (so descendants that
 * peek at theme — e.g. ClassroomCard's mascot preview — still work),
 * and lays the FIXED auth-surface bg behind everything.
 *
 * The hub greeting ("Good evening, …") uses the calm `login2-bg.png`
 * variant, shared with the classroom-landing page. LoginScreen +
 * SetupNotice stay on the original `login-bg.png`. No per-session
 * theme rotation on the hub. Once a classroom is *activated* (not just
 * selected), <ClassroomBg/> takes over and swaps in the classroom's
 * theme bg for live mode.
 */
export function SessionBg({
  registry,
  fallbackKey = 'jungle',
  allowedThemeKeys,
  children,
}: SessionBgProps) {
  const themeKey = useMemo(() => getSessionThemeKey(fallbackKey), [fallbackKey]);
  return (
    <ThemeProvider
      registry={registry}
      themeKey={themeKey}
      fallbackKey={fallbackKey}
      allowedThemeKeys={allowedThemeKeys}
      setGlobalAccent
    >
      <div
        className={styles.bg}
        style={{ backgroundImage: `url(${sessionBg})` }}
        aria-hidden="true"
      />
      {children}
    </ThemeProvider>
  );
}
